/**
 * Lens Selector Component
 * Handles hierarchical lens selection for glasses products
 */

if (!customElements.get('lens-selector')) {
  customElements.define(
    'lens-selector',
    class LensSelector extends HTMLElement {
      constructor() {
        super();
        this.config = null;
        this.currentStepConfig = null; // Store step config directly, no step names needed
        this.selectedPath = [];
        this.selectedTags = [];
        this.selectedLens = null;
        this.selectedProductForVariant = null; // Store product when showing variant selection
        this.prescriptionData = null;
        this.prescriptionMethod = null; // 'upload' or 'enter'
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.sectionId;
        
        this.stepHistory = [];
        this._trackingHistory = true; // Enable history tracking by default
        
        this.init();
      }

      init() {
        this.loadConfig();
        this.bindEvents();
        // Ensure modal is closed on init
        this.closeModal();
        // Ensure selected display is hidden on init (unless lens is already selected)
        const selectedDisplay = document.getElementById(`LensSelectorSelected-${this.sectionId}`);
        if (selectedDisplay && !this.dataset.selectedLensId) {
          selectedDisplay.hidden = true;
        }
      }

      loadConfig() {
        const configEl = document.getElementById(`LensSelectorConfig-${this.sectionId}`);
        if (configEl) {
          try {
            this.config = JSON.parse(configEl.textContent);
          } catch (error) {
            console.error('Error parsing lens selector config:', error);
            this.config = { paths: {} };
          }
        } else {
          console.warn('Lens selector config element not found');
          this.config = { paths: {} };
        }
      }

      bindEvents() {
        // Open modal
        const triggerBtn = document.getElementById(`LensSelectorButton-${this.sectionId}`);
        if (triggerBtn) {
          triggerBtn.addEventListener('click', () => this.openModal());
        }

        // Get modal element for event delegation
        const modal = document.getElementById(`LensSelectorModal-${this.sectionId}`);
        
        if (modal) {
          // Use event delegation for modal events - works even when modal is moved to body
          // Overlay click
          modal.addEventListener('click', (e) => {
            if (e.target.classList.contains('lens-selector__overlay')) {
              this.closeModal();
            }
          });

          // Close button
          modal.addEventListener('click', (e) => {
            if (e.target.closest(`#LensSelectorClose-${this.sectionId}`)) {
              this.closeModal();
            }
          });

          // Step 1 options - use event delegation
          modal.addEventListener('click', (e) => {
            const option = e.target.closest('[data-step="1"] .lens-selector__option');
            if (option) {
              const path = option.dataset.path;
              // Get tags from JSON config, fallback to data-tags if config not available
              let tags = [];
              if (this.config && this.config.paths && this.config.paths[path]) {
                // Use tags from JSON config
                tags = this.config.paths[path].tags || [];
              } else if (option.dataset.tags) {
                // Fallback to data-tags attribute if config not loaded
                tags = option.dataset.tags.split(',').filter(t => t.trim());
              }
              this.selectPath(path, tags);
            }
          });

          // Back buttons - use event delegation
          modal.addEventListener('click', (e) => {
            if (e.target.closest('[data-action="back"]')) {
              this.goBack();
            }
          });

          // Confirm button - use event delegation
          modal.addEventListener('click', (e) => {
            if (e.target.closest(`#LensSelectorConfirm-${this.sectionId}`)) {
              this.confirmSelection();
            }
          });
        }

        // Escape key to close
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.isHidden()) {
            this.closeModal();
          }
        });
      }

      openModal() {
        const modal = document.getElementById(`LensSelectorModal-${this.sectionId}`);
        if (modal) {
          // Move modal to body to avoid clipping by parent containers
          if (modal.parentElement !== document.body) {
            this._originalModalParent = modal.parentElement;
            document.body.appendChild(modal);
          }
          modal.removeAttribute('hidden');
          modal.hidden = false;
          document.body.style.overflow = 'hidden';
          
          // Only reset if no lens is currently selected (starting new selection)
          // If lens is already selected, user is changing selection, so preserve current state
          if (!this.selectedLens && !this.dataset.selectedLensId) {
            this.reset();
          } else {
            // Just show the initial step, but don't clear selection
            this.currentStepConfig = { type: 'initial' };
            this.showStepByType('initial');
          }
        }
      }

      closeModal() {
        const modal = document.getElementById(`LensSelectorModal-${this.sectionId}`);
        if (modal) {
          modal.setAttribute('hidden', '');
          modal.hidden = true;
          document.body.style.overflow = '';
          // Move modal back to original parent if it was moved
          if (this._originalModalParent && modal.parentElement === document.body) {
            this._originalModalParent.appendChild(modal);
            this._originalModalParent = null;
          }
        }
      }

      isHidden() {
        const modal = document.getElementById(`LensSelectorModal-${this.sectionId}`);
        return modal ? modal.hidden : true;
      }

      // Helper method to get modal element (works even when moved to body)
      getModal() {
        return document.getElementById(`LensSelectorModal-${this.sectionId}`);
      }

      reset() {
        this.currentStepConfig = { type: 'initial' }; // Set to initial step config
        this.selectedPath = [];
        this.selectedTags = [];
        this.selectedLens = null;
        this.stepHistory = []; // Clear history when resetting
        this._trackingHistory = true; // Re-enable history tracking
        this.showStepByType('initial');
        this.updateConfirmButton();
        
        // Hide selected display when resetting
        const selectedDisplay = document.getElementById(`LensSelectorSelected-${this.sectionId}`);
        const selectedImage = document.getElementById(`LensSelectorImageImg-${this.sectionId}`);
        const selectedPrice = document.getElementById(`LensSelectorPrice-${this.sectionId}`);
        const selectedValue = document.getElementById(`LensSelectorValue-${this.sectionId}`);
        
        if (selectedDisplay) selectedDisplay.hidden = true;
        if (selectedImage) {
          selectedImage.src = '';
          selectedImage.style.display = 'none';
        }
        if (selectedPrice) selectedPrice.textContent = '';
        if (selectedValue) selectedValue.textContent = '';
        
        // Reset button text
        const triggerBtn = document.getElementById(`LensSelectorButton-${this.sectionId}`);
        if (triggerBtn) {
          let span = triggerBtn.querySelector('span');
          if (span) {
            span.textContent = window.lensStrings?.select_lenses || 'Select Your Lenses';
          }
        }
        
        // Clear stored selection
        delete this.dataset.selectedLensId;
        delete this.dataset.selectedLensTitle;
        window.lensSelected = false;
      }

      selectPath(path, tags) {
        this.selectedPath.push(path);
        // If tags not provided, get them from JSON config
        const pathConfig = this.config.paths[path];
        if (!pathConfig) {
          console.warn(`selectPath: Path "${path}" not found in config`);
          return;
        }
        
        const tagsToUse = tags && tags.length > 0 ? tags : (pathConfig.tags || []);
        this.selectedTags = [...this.selectedTags, ...tagsToUse];

        // Fully dynamic flow:
        // 1. If nextStep is null/undefined/falsy, check requiresPrescription
        // 2. If requiresPrescription is true, show prescription step (then products)
        // 3. If requiresPrescription is false, show products directly
        // 4. If nextStep exists, navigate to it (will be a step config object)
        if (!pathConfig.nextStep || pathConfig.nextStep === null) {
          // Ensure currentStepConfig is set to initial if it's null
          if (!this.currentStepConfig || this.currentStepConfig.type !== 'initial') {
            this.currentStepConfig = { type: 'initial' };
          }
          
          if (pathConfig.requiresPrescription === true) {
            // Show prescription step, which will navigate to products after completion
            this.showPrescriptionStep();
          } else {
            // Go directly to products
            this.showProductsStep();
            const step6Title = document.getElementById(`LensSelectorStep6Title-${this.sectionId}`);
            if (step6Title) {
              // Use path name for title (e.g., "Easy Lens" -> "Select Your Easy Lens")
              const pathTitle = pathConfig.title || path.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              step6Title.textContent = window.lensStrings?.select_lens || `Select Your ${pathTitle}`;
            }
          }
          return;
        }

        // nextStep exists - navigate to it (will be a step config object)
        if (typeof pathConfig.nextStep === 'object' && pathConfig.nextStep !== null) {
          this.showStep(pathConfig.nextStep);
        } else {
          console.warn(`selectPath: Invalid nextStep for path "${path}"`, pathConfig.nextStep);
          // Fallback: show products
          this.showProductsStep();
        }
      }

      // Unified dynamic step renderer - fully dynamic from JSON
      // Uses a single container and changes content dynamically
      // No step names needed - works directly with step config
      showDynamicStep(stepConfig) {
        if (!stepConfig) {
          console.warn('showDynamicStep: stepConfig is required');
          return;
        }
        
        // Validate that stepConfig is an object, not a string
        if (typeof stepConfig === 'string') {
          console.error('showDynamicStep: Received string instead of step config object', stepConfig);
          return;
        }
        
        if (typeof stepConfig !== 'object') {
          console.error('showDynamicStep: Invalid step config type', typeof stepConfig, stepConfig);
          return;
        }
        
        // Store current step in history before navigating (unless we're going back)
        if (this._trackingHistory !== false && this.currentStepConfig) {
          this.stepHistory.push({
            config: this.currentStepConfig,
            tags: [...this.selectedTags] // Store tags at this point
          });
        }
        
        // Get the unified dynamic step container elements
        const stepTitle = document.getElementById(`LensSelectorDynamicStepTitle-${this.sectionId}`);
        const stepOptions = document.getElementById(`LensSelectorDynamicStepOptions-${this.sectionId}`);
        const modal = this.getModal();
        const dynamicStep = modal ? modal.querySelector('[data-step="dynamic"]') : null;

        // Update title
        if (stepTitle && stepConfig.title) {
          stepTitle.textContent = stepConfig.title;
        }

        // Handle different content types
        if (stepConfig.contentType === 'variant' && stepConfig.product) {
          // Render variant selection content - change container class for variant content
          if (stepOptions) {
            stepOptions.className = 'lens-selector__dynamic-content lens-selector__variant-selection';
            this.renderVariantContentInDynamicStep(stepOptions, stepConfig);
          }
        } else if (stepConfig.options) {
          // Ensure it's the options container for regular steps
          if (stepOptions) {
            stepOptions.className = 'lens-selector__options';
          }
          // Render options (standard dynamic step)
          if (stepOptions) {
            stepOptions.innerHTML = '';
            stepConfig.options.forEach(option => {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'lens-selector__option';
              btn.dataset.tags = option.tags?.join(',') || '';
              btn.innerHTML = `
                ${option.image ? `<img src="${option.image}" alt="${option.title}" class="lens-selector__option-image">` : ''}
                <span class="lens-selector__option-title">${option.title}</span>
                ${option.nextStep && typeof option.nextStep === 'object' && option.nextStep.options ? `<span class="lens-selector__option-description">${this.getStepDescription(option.nextStep)}</span>` : ''}
              `;
              btn.addEventListener('click', () => {
                const newTags = option.tags || [];
                this.selectedTags = [...this.selectedTags, ...newTags];
                
                // Use dynamic navigation
                const currentPath = this.selectedPath[0];
                const pathConfig = this.config.paths[currentPath];
                this.navigateToNextStep(option, pathConfig);
              });
              stepOptions.appendChild(btn);
            });
          }
        }

        // Hide all steps first
        if (modal) {
          modal.querySelectorAll('.lens-selector__step').forEach(step => {
            step.hidden = true;
          });
        }

        // Show the dynamic step container
        if (dynamicStep) {
          dynamicStep.hidden = false;
          dynamicStep.removeAttribute('hidden');
        }
        this.currentStepConfig = stepConfig;
      }

      // Generic step display function - works with any step config
      showStep(stepConfig) {
        // Validate that stepConfig is an object, not a string
        if (typeof stepConfig === 'string') {
          // If it's a special step type string, handle it appropriately
          if (stepConfig === 'step6' || stepConfig === 'products') {
            this.showProductsStep();
            return;
          }
          if (stepConfig === 'step5' || stepConfig === 'prescription') {
            this.showPrescriptionStep();
            return;
          }
          // Otherwise, ignore invalid string
          return;
        }
        // Only call showDynamicStep if it's a valid step config object
        if (stepConfig && typeof stepConfig === 'object') {
          this.showDynamicStep(stepConfig);
        }
      }

      getStepDescription(stepConfig) {
        if (!stepConfig || !stepConfig.options) return '';
        return stepConfig.options.map(opt => opt.title).join(', ');
      }

      showPrescriptionUpload() {
        const prescriptionMethodContainer = document.getElementById(`LensSelectorPrescriptionMethod-${this.sectionId}`);
        if (!prescriptionMethodContainer) return;
        
        // Render prescription upload component
        prescriptionMethodContainer.innerHTML = '';
        const uploadContainer = document.createElement('div');
        uploadContainer.className = 'prescription-upload';
        uploadContainer.id = `PrescriptionUpload-${this.sectionId}`;
        uploadContainer.innerHTML = `
          <div class="prescription-upload__instructions">
            <p>${window.lensStrings?.upload_instructions || 'Upload a clear image of your prescription. The image will be attached to your order.'}</p>
          </div>
          <div class="prescription-upload__dropzone" id="PrescriptionDropzone-${this.sectionId}">
            <input type="file" id="PrescriptionFileInput-${this.sectionId}" name="prescription_image" accept="image/*" class="prescription-upload__input" hidden>
            <div class="prescription-upload__preview" id="PrescriptionPreview-${this.sectionId}" hidden>
              <img id="PrescriptionPreviewImg-${this.sectionId}" src="" alt="" class="prescription-upload__preview-image">
              <button type="button" class="prescription-upload__remove" id="PrescriptionRemove-${this.sectionId}" hidden>
                <span>${window.lensStrings?.remove_image || 'Remove'}</span>
              </button>
            </div>
            <div class="prescription-upload__placeholder" id="PrescriptionPlaceholder-${this.sectionId}">
              <svg class="prescription-upload__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <p class="prescription-upload__text">${window.lensStrings?.drag_drop_or_click || 'Drag and drop your prescription image here, or click to browse'}</p>
              <button type="button" class="button button--secondary prescription-upload__browse">
                <span>${window.lensStrings?.browse_files || 'Browse Files'}</span>
              </button>
            </div>
          </div>
          <div class="prescription-upload__confirmation" id="PrescriptionConfirmation-${this.sectionId}" hidden>
            <p style="text-align: center;">${window.lensStrings?.image_uploaded || 'Prescription image uploaded successfully.'}</p>
            <div class="prescription-upload__actions" style="margin-top: 1.6rem;">
              <button type="button" class="button button--primary" id="PrescriptionConfirmUpload-${this.sectionId}">
                <span>${window.lensStrings?.confirm_and_continue || 'Confirm & Continue'}</span>
              </button>
            </div>
          </div>
          <div class="prescription-upload__error" id="PrescriptionError-${this.sectionId}" hidden>
            <p class="prescription-upload__error-message"></p>
            <button type="button" class="button button--secondary" id="PrescriptionRetry-${this.sectionId}">
              <span>${window.lensStrings?.try_again || 'Try Again'}</span>
            </button>
          </div>
        `;
        prescriptionMethodContainer.appendChild(uploadContainer);
        
        // Bind upload events
        this.bindPrescriptionUploadEvents();
      }

      showPrescriptionForm() {
        const prescriptionMethodContainer = document.getElementById(`LensSelectorPrescriptionMethod-${this.sectionId}`);
        if (!prescriptionMethodContainer) return;
        
        // Render prescription form
        prescriptionMethodContainer.innerHTML = '';
        const formContainer = document.createElement('div');
        formContainer.className = 'prescription-form';
        formContainer.id = `PrescriptionForm-${this.sectionId}`;
        
        // Create form HTML (simplified version, full form in snippet)
        formContainer.innerHTML = this.getPrescriptionFormHTML();
        prescriptionMethodContainer.appendChild(formContainer);
        
        // Bind form events
        this.bindPrescriptionFormEvents();
      }

      getPrescriptionFormHTML() {
        // Generate select options for SPH (-20.00 to +20.00) step 0.25
        let sphOptions = '<option value="">' + (window.lensStrings?.select_value || 'Select Value') + '</option>';
        for (let i = -20; i <= 20; i += 0.25) {
          sphOptions += `<option value="${i.toFixed(2)}">${i.toFixed(2)}</option>`;
        }

        // Generate select options for CYL (-10.00 to +10.00) step 0.25
        let cylOptions = '<option value="">' + (window.lensStrings?.select_value || 'Select Value') + '</option>';
        for (let i = -10; i <= 10; i += 0.25) {
          cylOptions += `<option value="${i.toFixed(2)}">${i.toFixed(2)}</option>`;
        }

        
        // Generate select options for Axis (1-180)
        let axisOptions = '<option value="">' + (window.lensStrings?.select_value || 'Select Value') + '</option>';
        for (let i = 1; i <= 180; i++) {
          axisOptions += `<option value="${i}">${i}</option>`;
        }
        
        // Generate select options for PD (20-40)
        let pdOptions = '<option value="">' + (window.lensStrings?.select_value || 'Select Value') + '</option>';
        for (let i = 20; i <= 40; i++) {
          pdOptions += `<option value="${i}">${i}</option>`;
        }
        
        // Generate select options for IPD (40-80)
        let ipdOptions = '<option value="">' + (window.lensStrings?.select_value || 'Select Value') + '</option>';
        for (let i = 40; i <= 80; i++) {
          ipdOptions += `<option value="${i}">${i}</option>`;
        }
        
        // Generate select options for Addition (0.00 to +6.00)
        let additionOptions = '<option value="">' + (window.lensStrings?.select_value || 'Select Value') + '</option>';
        for (let i = 0; i <= 6; i += 0.25) {
          additionOptions += `<option value="${i.toFixed(2)}">${i.toFixed(2)}</option>`;
        }
        
        return `
          <form class="prescription-form__form" id="PrescriptionFormForm-${this.sectionId}">
            <div class="prescription-form__section">
              <h4 class="prescription-form__section-title">${window.lensStrings?.od || 'OD (Right Eye)'}</h4>
              <div class="prescription-form__fields">
                <div class="prescription-form__field">
                  <label for="od-sph-${this.sectionId}">${window.lensStrings?.sph || 'SPH'}</label>
                  <select id="od-sph-${this.sectionId}" name="od_sph" class="prescription-form__select" required>${sphOptions}</select>
                </div>
                <div class="prescription-form__field">
                  <label for="od-cyl-${this.sectionId}">${window.lensStrings?.cyl || 'CYL'}</label>
                  <select id="od-cyl-${this.sectionId}" name="od_cyl" class="prescription-form__select" required>${cylOptions}</select>
                </div>
                <div class="prescription-form__field">
                  <label for="od-axis-${this.sectionId}">${window.lensStrings?.axis || 'Axis'}</label>
                  <select id="od-axis-${this.sectionId}" name="od_axis" class="prescription-form__select">${axisOptions}</select>
                </div>
              </div>
            </div>
            <div class="prescription-form__section">
              <h4 class="prescription-form__section-title">${window.lensStrings?.os || 'OS (Left Eye)'}</h4>
              <div class="prescription-form__fields">
                <div class="prescription-form__field">
                  <label for="os-sph-${this.sectionId}">${window.lensStrings?.sph || 'SPH'}</label>
                  <select id="os-sph-${this.sectionId}" name="os_sph" class="prescription-form__select" required>${sphOptions}</select>
                </div>
                <div class="prescription-form__field">
                  <label for="os-cyl-${this.sectionId}">${window.lensStrings?.cyl || 'CYL'}</label>
                  <select id="os-cyl-${this.sectionId}" name="os_cyl" class="prescription-form__select" required>${cylOptions}</select>
                </div>
                <div class="prescription-form__field">
                  <label for="os-axis-${this.sectionId}">${window.lensStrings?.axis || 'Axis'}</label>
                  <select id="os-axis-${this.sectionId}" name="os_axis" class="prescription-form__select">${axisOptions}</select>
                </div>
              </div>
            </div>
            <div class="prescription-form__section">
              <h4 class="prescription-form__section-title">${window.lensStrings?.pd || 'PD (Pupillary Distance)'}</h4>
              <div class="prescription-form__fields">
                <div class="prescription-form__field">
                  <label for="left-pd-${this.sectionId}">${window.lensStrings?.left_pd || 'Left PD'}</label>
                  <select id="left-pd-${this.sectionId}" name="left_pd" class="prescription-form__select">${pdOptions}</select>
                </div>
                <div class="prescription-form__field">
                  <label for="right-pd-${this.sectionId}">${window.lensStrings?.right_pd || 'Right PD'}</label>
                  <select id="right-pd-${this.sectionId}" name="right_pd" class="prescription-form__select">${pdOptions}</select>
                </div>
                <div class="prescription-form__field">
                  <label for="ipd-${this.sectionId}">${window.lensStrings?.interpupillary_distance || 'Interpupillary Distance'}</label>
                  <select id="ipd-${this.sectionId}" name="ipd" class="prescription-form__select">${ipdOptions}</select>
                </div>
              </div>
            </div>
            <div class="prescription-form__section">
              <div class="prescription-form__field">
                <label for="addition-${this.sectionId}">${window.lensStrings?.addition || 'Addition'}</label>
                <select id="addition-${this.sectionId}" name="addition" class="prescription-form__select">${additionOptions}</select>
              </div>
            </div>
            <div class="prescription-form__actions">
              <button type="submit" class="button button--primary prescription-form__submit">
                <span>${window.lensStrings?.continue || 'Continue'}</span>
              </button>
            </div>
          </form>
        `;
      }

      bindPrescriptionFormEvents() {
        const form = document.getElementById(`PrescriptionFormForm-${this.sectionId}`);
        if (form) {
          form.addEventListener('submit', (e) => {
            e.preventDefault();
            const formData = new FormData(form);
            this.prescriptionData = {
              od: {
                sph: formData.get('od_sph'),
                cyl: formData.get('od_cyl'),
                axis: formData.get('od_axis')
              },
              os: {
                sph: formData.get('os_sph'),
                cyl: formData.get('os_cyl'),
                axis: formData.get('os_axis')
              },
              pd: {
                left: formData.get('left_pd'),
                right: formData.get('right_pd'),
                ipd: formData.get('ipd')
              },
              addition: formData.get('addition'),
              method: 'enter'
            };
            // Ensure prescription step is in currentStepConfig before navigating to products
            if (!this.currentStepConfig || this.currentStepConfig.type !== 'prescription') {
              this.currentStepConfig = { type: 'prescription' };
            }
            // Navigate to step6 (products) after prescription is entered
            this.showProductsStep();
          });
        }
      }

      bindPrescriptionUploadEvents() {
        const fileInput = document.getElementById(`PrescriptionFileInput-${this.sectionId}`);
        const dropzone = document.getElementById(`PrescriptionDropzone-${this.sectionId}`);
        const browseBtn = dropzone?.querySelector('.prescription-upload__browse');
        const placeholder = document.getElementById(`PrescriptionPlaceholder-${this.sectionId}`);
        const preview = document.getElementById(`PrescriptionPreview-${this.sectionId}`);
        const previewImg = document.getElementById(`PrescriptionPreviewImg-${this.sectionId}`);
        const removeBtn = document.getElementById(`PrescriptionRemove-${this.sectionId}`);
        const processing = document.getElementById(`PrescriptionProcessing-${this.sectionId}`);
        const errorDiv = document.getElementById(`PrescriptionError-${this.sectionId}`);
        const retryBtn = document.getElementById(`PrescriptionRetry-${this.sectionId}`);
        
        if (browseBtn && fileInput) {
          browseBtn.addEventListener('click', () => fileInput.click());
        }
        
        if (fileInput) {
          fileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (file) {
              this.handlePrescriptionImage(file);
            }
          });
        }
        
        if (dropzone) {
          dropzone.addEventListener('dragover', (e) => {
            e.preventDefault();
            dropzone.classList.add('is-dragover');
          });
          
          dropzone.addEventListener('dragleave', () => {
            dropzone.classList.remove('is-dragover');
          });
          
          dropzone.addEventListener('drop', (e) => {
            e.preventDefault();
            dropzone.classList.remove('is-dragover');
            const file = e.dataTransfer.files[0];
            if (file && file.type.startsWith('image/')) {
              this.handlePrescriptionImage(file);
            }
          });
        }
        
        if (removeBtn) {
          removeBtn.addEventListener('click', () => {
            const confirmation = document.getElementById(`PrescriptionConfirmation-${this.sectionId}`);
            if (fileInput) fileInput.value = '';
            if (preview) preview.hidden = true;
            if (placeholder) placeholder.hidden = false;
            if (confirmation) confirmation.hidden = true;
            // Clear the image source
            if (previewImg) previewImg.src = '';
            // Hide the remove button when image is removed
            removeBtn.hidden = true;
            removeBtn.setAttribute('hidden', '');
            this.prescriptionData = null;
          });
        }
        
        // Confirm button - proceed to next step
        const confirmBtn = document.getElementById(`PrescriptionConfirmUpload-${this.sectionId}`);
        if (confirmBtn) {
          confirmBtn.addEventListener('click', () => {
            // Ensure prescription step is in currentStepConfig before navigating to products
            if (!this.currentStepConfig || this.currentStepConfig.type !== 'prescription') {
              this.currentStepConfig = { type: 'prescription' };
            }
            // Navigate directly to products step since prescription is complete
            this.showProductsStep();
          });
        }
        
        // Change image button - reset upload
        const changeImageBtn = document.getElementById(`PrescriptionChangeImage-${this.sectionId}`);
        
        if (retryBtn) {
          retryBtn.addEventListener('click', () => {
            if (errorDiv) errorDiv.hidden = true;
            if (placeholder) placeholder.hidden = false;
            if (fileInput) fileInput.value = '';
          });
        }
      }

      async handlePrescriptionImage(file) {
        const placeholder = document.getElementById(`PrescriptionPlaceholder-${this.sectionId}`);
        const preview = document.getElementById(`PrescriptionPreview-${this.sectionId}`);
        const previewImg = document.getElementById(`PrescriptionPreviewImg-${this.sectionId}`);
        const confirmation = document.getElementById(`PrescriptionConfirmation-${this.sectionId}`);
        const errorDiv = document.getElementById(`PrescriptionError-${this.sectionId}`);
        
        // Validate file
        if (!file.type.startsWith('image/')) {
          this.showPrescriptionError(window.lensStrings?.invalid_image || 'Invalid image file. Please upload an image.');
          return;
        }
        
        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          this.showPrescriptionError(window.lensStrings?.image_too_large || 'Image is too large. Please upload an image smaller than 10MB.');
          return;
        }
        
        // Show preview and store image file
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageDataUrl = e.target.result;
          
          if (previewImg) previewImg.src = imageDataUrl;
          if (preview) preview.hidden = false;
          if (placeholder) placeholder.hidden = true;
          if (errorDiv) errorDiv.hidden = true;
          if (confirmation) confirmation.hidden = false;
          
          // Show the remove button when image is uploaded
          const removeBtn = document.getElementById(`PrescriptionRemove-${this.sectionId}`);
          if (removeBtn) {
            removeBtn.hidden = false;
            removeBtn.removeAttribute('hidden');
          }
          
          // Store prescription data with image file (not base64)
          // This will be uploaded as a file to Shopify, which will return a proper URL
          this.prescriptionData = {
            method: 'upload',
            imageFile: file, // Store the actual File object for upload
            imageName: file.name,
            imageSize: file.size,
            imagePreview: imageDataUrl // Keep preview URL for display
          };
        };
        reader.readAsDataURL(file);
      }

      async waitForTesseract(maxWaitTime = 10000) {
        // Wait for Tesseract.js to be available
        const startTime = Date.now();
        
        while (typeof Tesseract === 'undefined' && (Date.now() - startTime) < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // If still not available, try loading it dynamically
        if (typeof Tesseract === 'undefined') {
          console.log('Tesseract.js not found, attempting to load dynamically...');
          await this.loadTesseractDynamically();
          
          // Wait a bit more after dynamic load
          const retryStartTime = Date.now();
          while (typeof Tesseract === 'undefined' && (Date.now() - retryStartTime) < 5000) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        
        if (typeof Tesseract === 'undefined') {
          throw new Error('Tesseract.js library failed to load. Please refresh the page and try again.');
        }
        
        return Tesseract;
      }

      async loadTesseractDynamically() {
        return new Promise((resolve, reject) => {
          // Check if script is already being loaded
          if (document.querySelector('script[src*="tesseract"]')) {
            resolve();
            return;
          }

          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
          script.async = true;
          script.onload = () => {
            console.log('Tesseract.js loaded dynamically');
            resolve();
          };
          script.onerror = () => {
            console.error('Failed to load Tesseract.js dynamically');
            reject(new Error('Failed to load OCR library'));
          };
          document.head.appendChild(script);
        });
      }

      async preprocessImageForOCR(imageFile) {
        return new Promise((resolve) => {
          const img = new Image();
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          img.onload = () => {
            // Set canvas size
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Draw image
            ctx.drawImage(img, 0, 0);
            
            // Get image data
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            
            // Convert to grayscale and enhance contrast
            for (let i = 0; i < data.length; i += 4) {
              // Convert to grayscale
              const gray = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
              
              // Enhance contrast (increase difference between light and dark)
              let enhanced = gray;
              if (gray < 128) {
                // Darken dark areas
                enhanced = gray * 0.7;
              } else {
                // Lighten light areas
                enhanced = 128 + (gray - 128) * 1.3;
              }
              
              // Clamp values
              enhanced = Math.max(0, Math.min(255, enhanced));
              
              // Apply to all channels
              data[i] = enhanced;
              data[i + 1] = enhanced;
              data[i + 2] = enhanced;
              // Alpha stays the same
            }
            
            // Put processed image data back
            ctx.putImageData(imageData, 0, 0);
            
            // Convert to blob
            canvas.toBlob((blob) => {
              resolve(blob);
            }, 'image/png');
          };
          
          img.src = URL.createObjectURL(imageFile);
        });
      }

      isOCRTextValid(text) {
        if (!text || text.trim().length < 10) {
          return false;
        }
        
        // Check if text contains too many special characters (garbled text indicator)
        const specialCharCount = (text.match(/[^a-zA-Z0-9\s.,:;+\-()]/g) || []).length;
        const totalChars = text.length;
        const specialCharRatio = specialCharCount / totalChars;
        
        // If more than 30% special characters, likely garbled
        if (specialCharRatio > 0.3) {
          return false;
        }
        
        // Check for common prescription keywords
        const keywords = ['SPH', 'CYL', 'AXIS', 'OD', 'OS', 'RIGHT', 'LEFT', 'PD', 'IPD', 'PRESCRIPTION', 'SPECTACLES'];
        const upperText = text.toUpperCase();
        const hasKeywords = keywords.some(keyword => upperText.includes(keyword));
        
        // Check for numbers (prescriptions should have numbers)
        const hasNumbers = /\d/.test(text);
        
        return hasKeywords || hasNumbers;
      }

      async extractPrescriptionData(imageFile) {
        const processingText = document.getElementById(`PrescriptionProcessingText-${this.sectionId}`);
        const progressDiv = document.getElementById(`PrescriptionProgress-${this.sectionId}`);
        const progressFill = document.getElementById(`PrescriptionProgressFill-${this.sectionId}`);
        const progressText = document.getElementById(`PrescriptionProgressText-${this.sectionId}`);

        try {
          // Show progress bar
          if (progressDiv) progressDiv.hidden = false;
          if (processingText) {
            processingText.textContent = window.lensStrings?.preprocessing_image || 'Preprocessing image for better OCR accuracy...';
          }

          // Wait for Tesseract to be available
          const TesseractLib = await this.waitForTesseract();
          
          // Preprocess image for better OCR
          if (processingText) {
            processingText.textContent = window.lensStrings?.preprocessing_image || 'Preprocessing image...';
          }
          const processedImage = await this.preprocessImageForOCR(imageFile);
          
          if (processingText) {
            processingText.textContent = window.lensStrings?.extracting_data || 'Extracting prescription data...';
          }

          // Perform OCR on the preprocessed image with better options
          const { data: { text, confidence } } = await TesseractLib.recognize(
            processedImage,
            'eng',
            {
              logger: (m) => {
                // Update progress UI
                if (m.status === 'recognizing text' && m.progress) {
                  const progress = Math.round(m.progress * 100);
                  if (progressFill) {
                    progressFill.style.width = `${progress}%`;
                  }
                  if (progressText) {
                    progressText.textContent = `${progress}%`;
                  }
                  console.log(`OCR Progress: ${progress}%`);
                } else if (m.status === 'loading tesseract core') {
                  if (processingText) {
                    processingText.textContent = window.lensStrings?.loading_ocr || 'Loading OCR engine...';
                  }
                } else if (m.status === 'initializing tesseract') {
                  if (processingText) {
                    processingText.textContent = window.lensStrings?.initializing_ocr || 'Initializing OCR...';
                  }
                }
              },
              // OCR options for better accuracy
              tessedit_char_whitelist: '0123456789.+-ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz :;=',
              tessedit_pageseg_mode: TesseractLib.PSM.AUTO
            }
          );

          // Hide progress bar
          if (progressDiv) progressDiv.hidden = true;
          if (processingText) {
            processingText.textContent = window.lensStrings?.parsing_data || 'Parsing prescription data...';
          }

          console.log('OCR Text:', text);
          console.log('OCR Confidence:', confidence);

          // Validate OCR text quality
          if (!this.isOCRTextValid(text)) {
            console.warn('OCR text appears to be garbled or invalid');
            throw new Error('OCR could not read the prescription clearly. Please try a clearer image or enter the prescription manually.');
          }

          // Check confidence level
          if (confidence < 30) {
            console.warn('OCR confidence is very low:', confidence);
            throw new Error('OCR confidence is low. Please try a clearer image or enter the prescription manually.');
          }

          // Parse prescription data from OCR text
          const prescriptionData = this.parsePrescriptionFromText(text);

          if (!prescriptionData) {
            throw new Error('Could not extract prescription values from the image. Please enter the prescription manually.');
          }

          return prescriptionData;
        } catch (error) {
          // Hide progress bar on error
          if (progressDiv) progressDiv.hidden = true;
          console.error('OCR Error:', error);
          
          // Provide more specific error message
          if (error.message.includes('Tesseract.js library failed to load')) {
            throw new Error('OCR library failed to load. Please refresh the page and try again.');
          }
          
          // Re-throw the error message (already user-friendly)
          throw error;
        }
      }

      parsePrescriptionFromText(text) {
        // Keep original text for better number extraction
        // Only normalize case and whitespace, don't corrupt numbers
        let normalizedText = text.toUpperCase();
        normalizedText = normalizedText.replace(/\s+/g, ' ').trim();
        
        // Keep original text for number extraction (preserves decimal points and signs)
        const originalText = text;
        
        const result = {
          od: { sph: null, cyl: null, axis: null },
          os: { sph: null, cyl: null, axis: null },
          pd: { left: null, right: null, ipd: null },
          addition: null
        };

        // Helper function to extract all decimal numbers from a string (preserve original text)
        const extractAllNumbers = (str, useOriginal = false) => {
          const sourceText = useOriginal ? originalText : str;
          // Match decimal numbers, including negative signs and plus signs
          // Be careful with patterns like "12-50" which might be "-12.50"
          const matches = sourceText.match(/([+-]?\d+\.?\d*)/g);
          if (!matches) return [];
          
          return matches.map(m => {
            // Handle cases like "12-50" which might be "-12.50" in prescriptions
            if (m.includes('-') && !m.startsWith('-') && !m.startsWith('+')) {
              // Check if it's a range or a negative number
              const parts = m.split('-');
              if (parts.length === 2 && parts[0].length > 0 && parts[1].length > 0) {
                // Could be negative number like "12-50" = "-12.50"
                const first = parseFloat(parts[0]);
                const second = parseFloat(parts[1]);
                if (first > 0 && second > 0 && second < 100) {
                  // Likely a negative number format
                  return parseFloat(`-${parts[0]}.${parts[1]}`);
                }
              }
            }
            return parseFloat(m);
          });
        };

        // Helper function to check if a number looks like a prescription value
        const isValidPrescriptionValue = (num, type) => {
          if (type === 'sph') {
            return Math.abs(num) <= 20 && num !== 0;
          } else if (type === 'cyl') {
            return Math.abs(num) <= 6;
          } else if (type === 'axis') {
            return num >= 0 && num <= 180 && Number.isInteger(num);
          } else if (type === 'pd') {
            return num >= 50 && num <= 80; // Typical PD range
          } else if (type === 'addition') {
            return num >= 0.5 && num <= 4.0;
          }
          return false;
        };

        // Split text into lines for easier parsing (use original text to preserve numbers)
        const originalLines = originalText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        const lines = normalizedText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        
        // Also create a single-line version for pattern matching
        const singleLine = normalizedText.replace(/\n/g, ' ');
        const originalSingleLine = originalText.replace(/\n/g, ' ');

        // Pattern 1: Look for prescription table structure
        // Many prescriptions have a table with SPH, CYL, AXIS columns
        // Look for lines containing these headers and extract values from nearby rows
        
        // Find lines with SPH, CYL, AXIS headers
        let sphIndex = -1, cylIndex = -1, axisIndex = -1;
        let rightEyeRowIndex = -1, leftEyeRowIndex = -1;
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          // Look for column headers
          if (line.includes('SPH') && sphIndex === -1) sphIndex = i;
          if (line.includes('CYL') && cylIndex === -1) cylIndex = i;
          if (line.includes('AXIS') && axisIndex === -1) axisIndex = i;
          
          // Look for eye labels
          if ((line.includes('RIGHT') || line.includes('OD') || line.includes('RE')) && rightEyeRowIndex === -1) {
            rightEyeRowIndex = i;
          }
          if ((line.includes('LEFT') || line.includes('OS') || line.includes('LE')) && leftEyeRowIndex === -1) {
            leftEyeRowIndex = i;
          }
        }
        
        // Pattern 2: Look for OD/OS labels with values (more flexible patterns)
        // Common patterns: "OD: -2.50 -1.00 180" or "OD -2.50 -1.00 180"
        const odPatterns = [
          /(?:OD|RIGHT|RE)[:\s\-]+([+-]?\d+\.?\d*)[\s\-]+([+-]?\d+\.?\d*)[\s\-]+(\d+)/i,
          /(?:OD|RIGHT|RE)[:\s]+([+-]?\d+\.?\d*)[\s]+([+-]?\d+\.?\d*)[\s]+(\d+)/i
        ];
        
        const osPatterns = [
          /(?:OS|LEFT|LE)[:\s\-]+([+-]?\d+\.?\d*)[\s\-]+([+-]?\d+\.?\d*)[\s\-]+(\d+)/i,
          /(?:OS|LEFT|LE)[:\s]+([+-]?\d+\.?\d*)[\s]+([+-]?\d+\.?\d*)[\s]+(\d+)/i
        ];

        // Extract from table structure if found
        if (sphIndex !== -1 && (rightEyeRowIndex !== -1 || leftEyeRowIndex !== -1)) {
          // Look for data rows near the header row
          for (let i = Math.max(sphIndex, rightEyeRowIndex, leftEyeRowIndex); i < Math.min(lines.length, i + 10); i++) {
            const originalLine = originalLines[i] || '';
            const line = lines[i] || '';
            const numbers = extractAllNumbers(originalLine, true);
            
            // Skip header rows
            if (line.includes('SPH') || line.includes('CYL') || line.includes('AXIS') || 
                line.includes('RIGHT') || line.includes('LEFT') || line.includes('OD') || line.includes('OS')) {
              continue;
            }
            
            // If we have 3+ numbers that look like prescription values
            if (numbers.length >= 3) {
              for (let j = 0; j <= numbers.length - 3; j++) {
                const sph = numbers[j];
                const cyl = numbers[j + 1];
                const axis = Math.round(numbers[j + 2]);
                
                if (isValidPrescriptionValue(sph, 'sph') && 
                    isValidPrescriptionValue(cyl, 'cyl') && 
                    isValidPrescriptionValue(axis, 'axis')) {
                  
                  // Check if this line is associated with right or left eye
                  const prevLines = lines.slice(Math.max(0, i - 3), i).join(' ');
                  const isRightEye = prevLines.includes('RIGHT') || prevLines.includes('OD') || prevLines.includes('RE');
                  const isLeftEye = prevLines.includes('LEFT') || prevLines.includes('OS') || prevLines.includes('LE');
                  
                  if (isRightEye && !result.od.sph) {
                    result.od.sph = sph;
                    result.od.cyl = cyl;
                    result.od.axis = axis;
                  } else if (isLeftEye && !result.os.sph) {
                    result.os.sph = sph;
                    result.os.cyl = cyl;
                    result.os.axis = axis;
                  } else if (!result.od.sph && rightEyeRowIndex !== -1 && i > rightEyeRowIndex && i < (leftEyeRowIndex !== -1 ? leftEyeRowIndex : lines.length)) {
                    // Assume it's right eye if we're between right and left markers
                    result.od.sph = sph;
                    result.od.cyl = cyl;
                    result.od.axis = axis;
                  } else if (!result.os.sph && leftEyeRowIndex !== -1 && i > leftEyeRowIndex) {
                    // Assume it's left eye if we're after left marker
                    result.os.sph = sph;
                    result.os.cyl = cyl;
                    result.os.axis = axis;
                  }
                }
              }
            }
          }
        }
        
        // Pattern 3: Try direct OD/OS patterns if table extraction didn't work
        for (let i = 0; i < lines.length; i++) {
          const originalLine = originalLines[i] || '';
          const line = lines[i] || '';
          
          // Try OD patterns
          if (!result.od.sph) {
            for (const pattern of odPatterns) {
              const odMatch = originalLine.match(pattern);
              if (odMatch) {
                const sph = parseFloat(odMatch[1]);
                const cyl = parseFloat(odMatch[2]);
                const axis = parseInt(odMatch[3], 10);
                
                if (isValidPrescriptionValue(sph, 'sph') && 
                    isValidPrescriptionValue(cyl, 'cyl') && 
                    isValidPrescriptionValue(axis, 'axis')) {
                  result.od.sph = sph;
                  result.od.cyl = cyl;
                  result.od.axis = axis;
                  break;
                }
              }
            }
          }

          // Try OS patterns
          if (!result.os.sph) {
            for (const pattern of osPatterns) {
              const osMatch = originalLine.match(pattern);
              if (osMatch) {
                const sph = parseFloat(osMatch[1]);
                const cyl = parseFloat(osMatch[2]);
                const axis = parseInt(osMatch[3], 10);
                
                if (isValidPrescriptionValue(sph, 'sph') && 
                    isValidPrescriptionValue(cyl, 'cyl') && 
                    isValidPrescriptionValue(axis, 'axis')) {
                  result.os.sph = sph;
                  result.os.cyl = cyl;
                  result.os.axis = axis;
                  break;
                }
              }
            }
          }

          // Pattern 4: Look for SPH, CYL, AXIS labels separately in table format
          // "SPH: -2.50" or "SPH -2.50"
          if ((line.includes('OD') || line.includes('RIGHT') || line.includes('RE')) && !result.od.sph) {
            const sphMatch = originalLine.match(/SPH[:\s]+([+-]?\d+\.?\d*)/i);
            const cylMatch = originalLine.match(/CYL[:\s]+([+-]?\d+\.?\d*)/i);
            const axisMatch = originalLine.match(/AXIS[:\s]+(\d+)/i);
            
            if (sphMatch && isValidPrescriptionValue(parseFloat(sphMatch[1]), 'sph')) {
              result.od.sph = parseFloat(sphMatch[1]);
            }
            if (cylMatch && isValidPrescriptionValue(parseFloat(cylMatch[1]), 'cyl')) {
              result.od.cyl = parseFloat(cylMatch[1]);
            }
            if (axisMatch && isValidPrescriptionValue(parseInt(axisMatch[1], 10), 'axis')) {
              result.od.axis = parseInt(axisMatch[1], 10);
            }
          }

          if ((line.includes('OS') || line.includes('LEFT') || line.includes('LE')) && !result.os.sph) {
            const sphMatch = originalLine.match(/SPH[:\s]+([+-]?\d+\.?\d*)/i);
            const cylMatch = originalLine.match(/CYL[:\s]+([+-]?\d+\.?\d*)/i);
            const axisMatch = originalLine.match(/AXIS[:\s]+(\d+)/i);
            
            if (sphMatch && isValidPrescriptionValue(parseFloat(sphMatch[1]), 'sph')) {
              result.os.sph = parseFloat(sphMatch[1]);
            }
            if (cylMatch && isValidPrescriptionValue(parseFloat(cylMatch[1]), 'cyl')) {
              result.os.cyl = parseFloat(cylMatch[1]);
            }
            if (axisMatch && isValidPrescriptionValue(parseInt(axisMatch[1], 10), 'axis')) {
              result.os.axis = parseInt(axisMatch[1], 10);
            }
          }

          // Pattern 5: PD (Pupillary Distance)
          // "PD: 32/30" or "PD 32 30" or "IPD: 62" or "PD= 65MM"
          const pdMatch = originalLine.match(/PD[:\s=]+(\d+)[\/\s]+(\d+)/i);
          if (pdMatch) {
            const left = parseInt(pdMatch[1], 10);
            const right = parseInt(pdMatch[2], 10);
            if (isValidPrescriptionValue(left, 'pd') && isValidPrescriptionValue(right, 'pd')) {
              result.pd.left = left;
              result.pd.right = right;
              result.pd.ipd = left + right;
            }
            continue;
          }

          const ipdMatch = originalLine.match(/IPD[:\s=]+(\d+)/i);
          if (ipdMatch && !result.pd.ipd) {
            const ipd = parseInt(ipdMatch[1], 10);
            if (isValidPrescriptionValue(ipd, 'pd')) {
              result.pd.ipd = ipd;
            }
          }
          
          // Also check for "PD= 65MM" format
          const pdEqualsMatch = originalLine.match(/PD[:\s]*=[:\s]*(\d+)/i);
          if (pdEqualsMatch && !result.pd.ipd) {
            const ipd = parseInt(pdEqualsMatch[1], 10);
            if (isValidPrescriptionValue(ipd, 'pd')) {
              result.pd.ipd = ipd;
            }
          }

          // Pattern 6: Addition
          // "ADD: +2.00" or "ADD +2.00" or "ADDITION: +2.00"
          const addMatch = originalLine.match(/ADD(ITION)?[:\s]+([\+-]?\d+\.?\d*)/i);
          if (addMatch && !result.addition) {
            const add = parseFloat(addMatch[2]);
            if (isValidPrescriptionValue(add, 'addition')) {
              result.addition = add;
            }
          }
        }

        // Pattern 7: Alternative format - table-like structure (fallback)
        // Sometimes prescriptions are in a table format
        // Try to find numbers in sequence that might be prescription values
        if (!result.od.sph || !result.os.sph) {
          // Look for lines with multiple decimal numbers that could be prescription values
          const numberLines = originalLines.map((line, idx) => ({ line, original: line, idx })).filter(({ line }) => {
            const numbers = extractAllNumbers(line, true);
            return numbers.length >= 2; // At least 2 numbers (SPH and CYL minimum)
          });

          for (let i = 0; i < numberLines.length; i++) {
            const { line, original, idx } = numberLines[i];
            const numbers = extractAllNumbers(original, true);
            
            // Try to find valid prescription triplets (SPH, CYL, AXIS)
            for (let j = 0; j <= numbers.length - 3; j++) {
              const sph = numbers[j];
              const cyl = numbers[j + 1];
              const axis = Math.round(numbers[j + 2]);
              
              if (isValidPrescriptionValue(sph, 'sph') && 
                  isValidPrescriptionValue(cyl, 'cyl') && 
                  isValidPrescriptionValue(axis, 'axis')) {
                
                // Check if line mentions OD or OS
                if ((line.includes('OD') || line.includes('RIGHT') || line.includes('RE')) && !result.od.sph) {
                  result.od.sph = sph;
                  result.od.cyl = cyl;
                  result.od.axis = axis;
                  break;
                } else if ((line.includes('OS') || line.includes('LEFT') || line.includes('LE')) && !result.os.sph) {
                  result.os.sph = sph;
                  result.os.cyl = cyl;
                  result.os.axis = axis;
                  break;
                } else if (!result.od.sph && i === 0 && j === 0) {
                  // Assume first valid triplet is OD if not specified
                  result.od.sph = sph;
                  result.od.cyl = cyl;
                  result.od.axis = axis;
                } else if (!result.os.sph && (i === 1 || (i === 0 && result.od.sph))) {
                  // Assume second valid triplet is OS
                  result.os.sph = sph;
                  result.os.cyl = cyl;
                  result.os.axis = axis;
                  break;
                }
              }
            }
            
            // Also try pairs (SPH, CYL) if axis is on next line or missing
            if (numbers.length >= 2 && (!result.od.sph || !result.os.sph)) {
              const sph = numbers[0];
              const cyl = numbers[1];
              
              if (isValidPrescriptionValue(sph, 'sph') && isValidPrescriptionValue(cyl, 'cyl')) {
                // Look for axis in nearby lines
                let axis = null;
                if (idx < originalLines.length - 1) {
                  const nextLineNumbers = extractAllNumbers(originalLines[idx + 1], true);
                  for (const num of nextLineNumbers) {
                    if (isValidPrescriptionValue(num, 'axis')) {
                      axis = Math.round(num);
                      break;
                    }
                  }
                }
                
                const normalizedLine = lines[idx] || '';
                if ((normalizedLine.includes('OD') || normalizedLine.includes('RIGHT') || normalizedLine.includes('RE')) && !result.od.sph) {
                  result.od.sph = sph;
                  result.od.cyl = cyl;
                  if (axis !== null) result.od.axis = axis;
                } else if ((normalizedLine.includes('OS') || normalizedLine.includes('LEFT') || normalizedLine.includes('LE')) && !result.os.sph) {
                  result.os.sph = sph;
                  result.os.cyl = cyl;
                  if (axis !== null) result.os.axis = axis;
                }
              }
            }
          }
        }
        
        // Pattern 8: Search entire text for prescription values near keywords (last resort)
        // This handles cases where OCR has scattered the text
        if (!result.od.sph || !result.os.sph) {
          // Look for OD/OS keywords and extract nearby numbers
          const odIndex = singleLine.search(/OD|RIGHT|RE/i);
          const osIndex = singleLine.search(/OS|LEFT|LE/i);
          
          if (odIndex !== -1 && !result.od.sph) {
            // Find numbers within 100 characters after OD (use original text)
            const odSection = originalSingleLine.substring(odIndex, Math.min(odIndex + 150, originalSingleLine.length));
            const odNumbers = extractAllNumbers(odSection, true);
            
            for (let i = 0; i <= odNumbers.length - 3; i++) {
              const sph = odNumbers[i];
              const cyl = odNumbers[i + 1];
              const axis = Math.round(odNumbers[i + 2]);
              
              if (isValidPrescriptionValue(sph, 'sph') && 
                  isValidPrescriptionValue(cyl, 'cyl') && 
                  isValidPrescriptionValue(axis, 'axis')) {
                result.od.sph = sph;
                result.od.cyl = cyl;
                result.od.axis = axis;
                break;
              }
            }
          }
          
          if (osIndex !== -1 && !result.os.sph) {
            // Find numbers within 100 characters after OS (use original text)
            const osSection = originalSingleLine.substring(osIndex, Math.min(osIndex + 150, originalSingleLine.length));
            const osNumbers = extractAllNumbers(osSection, true);
            
            for (let i = 0; i <= osNumbers.length - 3; i++) {
              const sph = osNumbers[i];
              const cyl = osNumbers[i + 1];
              const axis = Math.round(osNumbers[i + 2]);
              
              if (isValidPrescriptionValue(sph, 'sph') && 
                  isValidPrescriptionValue(cyl, 'cyl') && 
                  isValidPrescriptionValue(axis, 'axis')) {
                result.os.sph = sph;
                result.os.cyl = cyl;
                result.os.axis = axis;
                break;
              }
            }
          }
        }

        // Validate that we extracted at least some data
        const hasData = result.od.sph !== null || result.os.sph !== null || 
                       result.pd.ipd !== null || result.addition !== null;

        if (!hasData) {
          console.warn('Could not extract prescription data from OCR text');
          console.log('OCR Text (original):', originalText);
          console.log('OCR Text (normalized):', normalizedText);
          console.log('Extracted numbers from text:', extractAllNumbers(originalSingleLine, true));
          console.log('Parsed result:', result);
          return null;
        }

        console.log('Successfully extracted prescription data:', result);
        return result;
      }

      showExtractedPrescriptionData(data) {
        const prescriptionMethodContainer = document.getElementById(`LensSelectorPrescriptionMethod-${this.sectionId}`);
        if (!prescriptionMethodContainer) return;
        
        const extractedDiv = document.createElement('div');
        extractedDiv.className = 'prescription-upload__extracted';
        extractedDiv.innerHTML = `
          <h4>${window.lensStrings?.extracted_data || 'Extracted Prescription Data'}</h4>
          <div class="prescription-upload__data">
            <div class="prescription-data__section">
              <strong>${window.lensStrings?.od || 'OD'}:</strong>
              SPH: ${data.od?.sph || 'N/A'}, CYL: ${data.od?.cyl || 'N/A'}, Axis: ${data.od?.axis || 'N/A'}
            </div>
            <div class="prescription-data__section">
              <strong>${window.lensStrings?.os || 'OS'}:</strong>
              SPH: ${data.os?.sph || 'N/A'}, CYL: ${data.os?.cyl || 'N/A'}, Axis: ${data.os?.axis || 'N/A'}
            </div>
            <div class="prescription-data__section">
              <strong>${window.lensStrings?.pd || 'PD'}:</strong>
              Left: ${data.pd?.left || 'N/A'}, Right: ${data.pd?.right || 'N/A'}, IPD: ${data.pd?.ipd || 'N/A'}
            </div>
            <div class="prescription-data__section">
              <strong>${window.lensStrings?.addition || 'Addition'}:</strong> ${data.addition || 'N/A'}
            </div>
          </div>
          <div class="prescription-upload__actions">
            <button type="button" class="button button--secondary" id="PrescriptionEdit-${this.sectionId}">
              ${window.lensStrings?.edit_manually || 'Edit Manually'}
            </button>
            <button type="button" class="button button--primary" id="PrescriptionConfirm-${this.sectionId}">
              <span>${window.lensStrings?.confirm_and_continue || 'Confirm & Continue'}</span>
            </button>
          </div>
        `;
        
        prescriptionMethodContainer.querySelector('.prescription-upload__extracted')?.remove();
        prescriptionMethodContainer.appendChild(extractedDiv);
        
        // Bind events
        document.getElementById(`PrescriptionEdit-${this.sectionId}`)?.addEventListener('click', () => {
          this.showPrescriptionForm();
        });
        
        document.getElementById(`PrescriptionConfirm-${this.sectionId}`)?.addEventListener('click', () => {
          // Ensure prescription step is in currentStepConfig before navigating to products
          if (!this.currentStepConfig || this.currentStepConfig.type !== 'prescription') {
            this.currentStepConfig = { type: 'prescription' };
          }
          // Navigate to step6 (products) after prescription is confirmed
          this.showProductsStep();
        });
      }

      showPrescriptionError(message, showManualEntry = false) {
        const prescriptionMethodContainer = document.getElementById(`LensSelectorPrescriptionMethod-${this.sectionId}`);
        const errorDiv = document.getElementById(`PrescriptionError-${this.sectionId}`);
        const errorMsg = errorDiv?.querySelector('.prescription-upload__error-message');
        
        if (errorDiv) errorDiv.hidden = false;
        if (errorMsg) errorMsg.textContent = message;
        
        // Add manual entry button if requested
        if (showManualEntry && prescriptionMethodContainer) {
          let manualBtn = errorDiv.querySelector('.prescription-upload__manual-entry');
          if (!manualBtn) {
            manualBtn = document.createElement('button');
            manualBtn.type = 'button';
            manualBtn.className = 'button button--primary prescription-upload__manual-entry';
            manualBtn.style.marginTop = '1rem';
            manualBtn.textContent = window.lensStrings?.enter_manually || 'Enter Manually';
            manualBtn.onclick = () => {
              this.showPrescriptionForm();
            };
            errorDiv.appendChild(manualBtn);
          }
        }
        
        // Bind retry button
        const retryBtn = errorDiv?.querySelector(`#PrescriptionRetry-${this.sectionId}`);
        if (retryBtn) {
          retryBtn.onclick = () => {
            const fileInput = document.getElementById(`PrescriptionFileInput-${this.sectionId}`);
            if (fileInput) fileInput.click();
          };
        }
      }

      async loadLensProducts() {
        const productsContainer = document.getElementById(`LensSelectorProducts-${this.sectionId}`);
        if (!productsContainer) return;

        // Ensure products step is shown
        // showProductsStep() already sets currentStepConfig and shows the step, so we just need to show it here
        this.showStepByType('products');

        productsContainer.innerHTML = `
          <div class="lens-selector__loading">
            <div class="loading__spinner"></div>
            <p>${window.lensStrings?.loading || 'Loading lenses...'}</p>
          </div>
        `;

        try {
          // Build search query - use q=* to get all products, then filter by tags in Liquid template
          // We encode tags in the query string with a special format that Liquid can parse
          // Format: q=*|TAGS:tag1,tag2|SPH:value|CYL:value - Liquid template will parse and filter server-side
          const tagQuery = this.selectedTags.join(',');
          
          // Get prescription SPH and CYL values if prescription data exists
          let prescriptionQuery = '';
          if (this.prescriptionData && (this.prescriptionData.od || this.prescriptionData.os)) {
            // Get SPH and CYL values from both eyes, use the maximum absolute values (most restrictive)
            let maxSph = null;
            let maxCyl = null;
            
            // Check OD (right eye)
            if (this.prescriptionData.od) {
              const odSph = parseFloat(this.prescriptionData.od.sph);
              const odCyl = parseFloat(this.prescriptionData.od.cyl);
              if (!isNaN(odSph)) {
                const absSph = Math.abs(odSph);
                if (maxSph === null || absSph > Math.abs(maxSph)) {
                  maxSph = odSph;
                }
              }
              if (!isNaN(odCyl)) {
                const absCyl = Math.abs(odCyl);
                if (maxCyl === null || absCyl > Math.abs(maxCyl)) {
                  maxCyl = odCyl;
                }
              }
            }
            
            // Check OS (left eye)
            if (this.prescriptionData.os) {
              const osSph = parseFloat(this.prescriptionData.os.sph);
              const osCyl = parseFloat(this.prescriptionData.os.cyl);
              if (!isNaN(osSph)) {
                const absSph = Math.abs(osSph);
                if (maxSph === null || absSph > Math.abs(maxSph)) {
                  maxSph = osSph;
                }
              }
              if (!isNaN(osCyl)) {
                const absCyl = Math.abs(osCyl);
                if (maxCyl === null || absCyl > Math.abs(maxCyl)) {
                  maxCyl = osCyl;
                }
              }
            }
            
            // Build prescription query string
            if (maxSph !== null && !isNaN(maxSph)) {
              prescriptionQuery += `|SPH:${maxSph}`;
            }
            if (maxCyl !== null && !isNaN(maxCyl)) {
              prescriptionQuery += `|CYL:${maxCyl}`;
            }
          }
          
          // Use search API with encoded tags and prescription in q parameter
          // Format: q=*|TAGS:tag1,tag2|SPH:value|CYL:value
          const queryString = tagQuery ? `*|TAGS:${tagQuery}${prescriptionQuery}` : `*${prescriptionQuery}`;
          const encodedQuery = encodeURIComponent(queryString);
          const searchUrl = `/search?q=${encodedQuery}&type=product&view=json`;

          const response = await fetch(searchUrl);
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          // Get response as text first to handle any formatting issues
          const responseText = await response.text();
          
          try {
            // Try to parse as JSON (even if content-type header is wrong)
            const data = JSON.parse(responseText);
            
            if (!data.products || !Array.isArray(data.products)) {
              throw new Error('Invalid response format: products array not found');
            }

            this.renderLensProductsFromJSON(productsContainer, data.products);
          } catch (parseError) {
            // If JSON parsing fails, check if it's HTML
            if (responseText.trim().startsWith('<')) {
              throw new Error('Search API returned HTML instead of JSON. Please ensure search.json.liquid template exists and the URL uses view=json parameter.');
            } else {
              console.error('JSON parse error:', parseError);
              console.error('Response text:', responseText.substring(0, 500));
              throw new Error(`Failed to parse JSON response: ${parseError.message}`);
            }
          }
        } catch (error) {
          console.error('Error loading lens products:', error);
          productsContainer.innerHTML = `
            <div class="lens-selector__error">
              <p>${window.lensStrings?.error || 'Unable to load lenses. Please try again.'}</p>
              <p style="font-size: 0.875rem; margin-top: 0.5rem; opacity: 0.7;">Error: ${error.message}</p>
            </div>
          `;
        }
      }


      renderLensProductsFromJSON(container, products) {
        if (products.length === 0) {
          container.innerHTML = `
            <div class="lens-selector__error">
              <p>${window.lensStrings?.noProducts || 'No lenses found matching your selection.'}</p>
            </div>
          `;
          return;
        }

        const grid = document.createElement('div');
        grid.className = 'lens-selector__products-grid';
        
        products.forEach(product => {
          if (!product.variants || product.variants.length === 0) return;

          // Filter to only in-stock variants
          const availableVariants = product.variants.filter(v => v.available === true);
          if (availableVariants.length === 0) return; // Skip products with no in-stock variants

          const defaultVariant = availableVariants[0];

          const card = document.createElement('div');
          card.className = 'lens-selector__product-card';
          card.dataset.productId = product.id;
          
          // Handle image - can be string URL or object with src property
          let imageUrl = '';
          if (product.images && product.images.length > 0) {
            const firstImage = product.images[0];
            if (typeof firstImage === 'string') {
              imageUrl = firstImage;
            } else if (firstImage.src) {
              imageUrl = firstImage.src;
            } else if (firstImage.url) {
              imageUrl = firstImage.url;
            }
            
            // Optimize image URL if it's a Shopify CDN URL (add size parameter)
            if (imageUrl && imageUrl.includes('cdn.shopify.com')) {
              // Replace size parameter if present, or add one
              imageUrl = imageUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048)\./, '_300x300.');
              // If no size parameter, add one before the file extension
              if (!imageUrl.match(/_\d+x\d+\./)) {
                imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_300x300.$1$2');
              }
            }
          }

          // Format price for default variant
          const defaultPriceInCents = this.convertPriceToCents(defaultVariant.price);
          const formattedPrice = this.formatMoney(defaultPriceInCents);

          card.innerHTML = `
            <div class="lens-selector__product-image-wrapper">
              ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" class="lens-selector__product-image" data-product-id="${product.id}" loading="lazy" onerror="this.style.display='none'">` : '<div class="lens-selector__product-image-placeholder"></div>'}
            </div>
            <div class="lens-selector__product-info">
              <h4 class="lens-selector__product-title">${this.escapeHtml(product.title)}</h4>
              <p class="lens-selector__product-price" data-product-id="${product.id}">${formattedPrice || 'Price unavailable'}</p>
            </div>
            <button type="button" class="lens-selector__product-select-btn" data-product-id="${product.id}" data-variant-id="${defaultVariant.id}">
              ${window.lensStrings?.select || 'Select'}
            </button>
          `;

          // Store product data for variant selection (only available variants)
          card.dataset.variants = JSON.stringify(availableVariants);
          card.dataset.selectedVariantId = defaultVariant.id;
          card.dataset.defaultImageUrl = imageUrl; // Store default image for fallback

          // Handle product selection - show variant step instead of immediately selecting
          const selectBtn = card.querySelector('.lens-selector__product-select-btn');
          if (selectBtn) {
            selectBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              // Store product data and show variant selection step (with only available variants)
              this.selectedProductForVariant = {
                ...product,
                variants: availableVariants, // Only include available variants
                defaultVariant: defaultVariant,
                defaultImageUrl: imageUrl
              };
              this.showVariantStep({ ...product, variants: availableVariants }, defaultVariant, imageUrl);
            });
          }

          grid.appendChild(card);
        });

        container.innerHTML = '';
        container.appendChild(grid);
        // Products step is already shown by showProductsStep(), no need to call showStep again
        this.updateConfirmButton();
      }

      buildVariantSelector(product, defaultVariantId) {
        if (!product.variants || product.variants.length <= 1) return '';

        const defaultVariant = product.variants.find(v => v.id === defaultVariantId) || product.variants[0];

        // Build color circles for variants
        // Assume variants are color-based (we'll show all variants as color options)
        let selectorHTML = '<div class="lens-selector__variant-selector">';
        selectorHTML += '<div class="lens-selector__variant-colors">';
        
        product.variants.forEach((variant, index) => {
          const isSelected = variant.id === defaultVariantId;
          const colorName = variant.option1 || variant.option2 || variant.option3 || `Option ${index + 1}`;
          const colorValue = this.getColorValue(colorName);
          
          selectorHTML += `
            <button 
              type="button" 
              class="lens-selector__variant-color ${isSelected ? 'is-selected' : ''}" 
              data-variant-id="${variant.id}"
              data-product-id="${product.id}"
              data-color-name="${this.escapeHtml(colorName)}"
              style="background-color: ${colorValue};"
              title="${this.escapeHtml(colorName)}"
              aria-label="${this.escapeHtml(colorName)}"
            >
              ${variant.image ? `<span class="lens-selector__variant-color-image" style="background-image: url('${variant.image}');"></span>` : ''}
            </button>
          `;
        });
        
        selectorHTML += '</div></div>';
        return selectorHTML;
      }

      getColorValue(colorName) {
        // Map common color names to hex values
        const colorMap = {
          'red': '#FF0000',
          'blue': '#0000FF',
          'green': '#00FF00',
          'yellow': '#FFFF00',
          'black': '#000000',
          'white': '#FFFFFF',
          'gray': '#808080',
          'grey': '#808080',
          'brown': '#A52A2A',
          'orange': '#FFA500',
          'purple': '#800080',
          'pink': '#FFC0CB',
          'navy': '#000080',
          'beige': '#F5F5DC',
          'tan': '#D2B48C',
          'silver': '#C0C0C0',
          'gold': '#FFD700',
        };

        const normalizedName = colorName.toLowerCase().trim();
        
        // Check if it's a hex color
        if (/^#[0-9A-F]{6}$/i.test(normalizedName)) {
          return normalizedName.toUpperCase();
        }
        
        // Check color map
        if (colorMap[normalizedName]) {
          return colorMap[normalizedName];
        }
        
        // Try to extract hex from color name (e.g., "Red #FF0000")
        const hexMatch = colorName.match(/#[0-9A-F]{6}/i);
        if (hexMatch) {
          return hexMatch[0].toUpperCase();
        }
        
        // Default to a neutral gray if color can't be determined
        return '#CCCCCC';
      }

      updateProductVariant(card, product, selectedVariantId) {
        const selectedVariant = product.variants.find(v => v.id === selectedVariantId);
        if (!selectedVariant) return;

        // Update stored variant ID
        card.dataset.selectedVariantId = selectedVariantId;

        // Update product image if variant has an image, otherwise use default
        const productImage = card.querySelector(`.lens-selector__product-image[data-product-id="${product.id}"]`);
        if (productImage) {
          let imageUrlToUse = card.dataset.defaultImageUrl; // Default to product's first image
          
          if (selectedVariant.image) {
            // Optimize variant image URL
            let variantImageUrl = selectedVariant.image;
            if (variantImageUrl && variantImageUrl.includes('cdn.shopify.com')) {
              variantImageUrl = variantImageUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048)\./, '_300x300.');
              if (!variantImageUrl.match(/_\d+x\d+\./)) {
                variantImageUrl = variantImageUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_300x300.$1$2');
              }
            }
            imageUrlToUse = variantImageUrl;
          }
          
          if (imageUrlToUse) {
            productImage.src = imageUrlToUse;
            productImage.style.display = 'block';
          }
        }

        // Update price display
        const priceElement = card.querySelector(`.lens-selector__product-price[data-product-id="${product.id}"]`);
        if (priceElement) {
          const priceInCents = this.convertPriceToCents(selectedVariant.price);
          const formattedPrice = this.formatMoney(priceInCents);
          priceElement.textContent = formattedPrice || 'Price unavailable';
        }

        // Update select button variant ID
        const selectBtn = card.querySelector('.lens-selector__product-select-btn');
        if (selectBtn) {
          selectBtn.dataset.variantId = selectedVariantId;
        }
      }

      findVariantByOptions(product, option1, option2, option3) {
        return product.variants.find(variant => {
          const match1 = !option1 || variant.option1 === option1;
          const match2 = !option2 || variant.option2 === option2;
          const match3 = !option3 || variant.option3 === option3;
          return match1 && match2 && match3;
        });
      }

      convertPriceToCents(price) {
        let priceInCents = price;
        
        // Convert to number if it's a string
        if (typeof priceInCents === 'string') {
          priceInCents = parseFloat(priceInCents);
        }
        
        // Shopify JSON API should return prices in cents, but let's verify
        // If price looks like it's in base currency (small number like 490), convert to cents
        // If price is already large (like 49000), it's already in cents
        if (typeof priceInCents === 'number' && !isNaN(priceInCents)) {
          // If price is less than 1000 and a whole number, it's likely in base currency
          // Example: 490 EGP should be 49000 cents
          if (priceInCents < 1000 && priceInCents > 0 && priceInCents % 1 === 0) {
            priceInCents = Math.round(priceInCents * 100);
          }
          // If price is a decimal less than 100 (e.g., 4.90), it's definitely in base currency
          else if (priceInCents < 100 && priceInCents % 1 !== 0) {
            priceInCents = Math.round(priceInCents * 100);
          }
          // Otherwise, assume it's already in cents
        }
        
        return priceInCents;
      }


      selectLens(lens) {
        this.selectedLens = lens;
        
        // Update UI to show selected state
        this.querySelectorAll('.lens-selector__product-card').forEach(card => {
          card.classList.remove('is-selected');
          const selectBtn = card.querySelector('.lens-selector__product-select-btn');
          if (selectBtn && selectBtn.dataset.variantId === lens.variantId.toString()) {
            card.classList.add('is-selected');
          }
        });

        this.updateConfirmButton();
      }

      updateConfirmButton() {
        const confirmBtn = document.getElementById(`LensSelectorConfirm-${this.sectionId}`);
        const footer = document.getElementById(`LensSelectorFooter-${this.sectionId}`);
        
        if (this.selectedLens) {
          if (confirmBtn) confirmBtn.disabled = false;
          if (footer) footer.hidden = false;
        } else {
          if (confirmBtn) confirmBtn.disabled = true;
          if (footer) footer.hidden = true;
        }
      }

      confirmSelection() {
        if (!this.selectedLens) return;

        // Store selection in component
        this.dataset.selectedLensId = this.selectedLens.variantId;
        this.dataset.selectedLensTitle = this.selectedLens.title;

        // Set global flag so inline handlers know lens is selected
        window.lensSelected = true;

        // Update selected display
        const selectedValue = document.getElementById(`LensSelectorValue-${this.sectionId}`);
        const selectedDisplay = document.getElementById(`LensSelectorSelected-${this.sectionId}`);
        const selectedImage = document.getElementById(`LensSelectorImageImg-${this.sectionId}`);
        const selectedPrice = document.getElementById(`LensSelectorPrice-${this.sectionId}`);
        const triggerBtn = document.getElementById(`LensSelectorButton-${this.sectionId}`);

        if (selectedValue) selectedValue.textContent = this.selectedLens.title;
        
        // Update image
        if (selectedImage && this.selectedLens.image) {
          selectedImage.src = this.selectedLens.image;
          selectedImage.alt = this.selectedLens.title;
          selectedImage.style.display = 'block';
        } else if (selectedImage) {
          selectedImage.style.display = 'none';
        }
        
        // Update price - ensure it's in cents format
        if (selectedPrice && this.selectedLens.price) {
          let priceInCents = this.selectedLens.price;
          // Convert to number if string
          if (typeof priceInCents === 'string') {
            priceInCents = parseFloat(priceInCents);
          }
          // If price is less than 1000, it's likely in base currency, convert to cents
          if (typeof priceInCents === 'number' && !isNaN(priceInCents) && priceInCents > 0) {
            if (priceInCents < 1000 && priceInCents % 1 === 0) {
              priceInCents = Math.round(priceInCents * 100);
            } else if (priceInCents < 100 && priceInCents % 1 !== 0) {
              priceInCents = Math.round(priceInCents * 100);
            }
          }
          const formattedPrice = this.formatMoney(priceInCents);
          selectedPrice.textContent = formattedPrice;
        }
        
        // Show the selected display
        if (selectedDisplay) selectedDisplay.hidden = false;
        if (triggerBtn) {
          // Preserve the span structure for proper styling
          let span = triggerBtn.querySelector('span');
          if (!span) {
            span = document.createElement('span');
            triggerBtn.appendChild(span);
          }
          span.textContent = window.lensStrings?.changeSelection || 'Change Selection';
        }

        // Dispatch event for product form with prescription data
        // Use composed: true to ensure it crosses shadow DOM boundaries if any
        const event = new CustomEvent('lens-selected', {
          detail: {
            lens: this.selectedLens,
            tags: this.selectedTags,
            path: this.selectedPath,
            prescriptionData: this.prescriptionData,
            prescriptionMethod: this.prescriptionMethod
          },
          bubbles: true,
          composed: true
        });
        
        console.log('Dispatching lens-selected event:', {
          lens: this.selectedLens,
          hasLens: !!this.selectedLens,
          variantId: this.selectedLens?.variantId
        });
        
        this.dispatchEvent(event);
        
        // Also dispatch on document as a fallback
        document.dispatchEvent(new CustomEvent('lens-selected', {
          detail: event.detail,
          bubbles: true,
          composed: true
        }));
        
        // Also directly update all add to cart buttons as a fallback
        // This ensures buttons are enabled even if event listeners aren't working
        const allButtons = document.querySelectorAll('[name="add"][data-is-glasses="true"], button[type="submit"][data-is-glasses="true"]');
        allButtons.forEach(button => {
          button.removeAttribute('disabled');
          button.disabled = false;
          button.classList.remove('lens-required');
          // Set data attribute to indicate lens is selected
          button.dataset.lensSelected = 'true';
          // Remove onclick handler since lens is selected
          button.removeAttribute('onclick');
          const span = button.querySelector('span');
          if (span) {
            span.textContent = window.variantStrings?.addToCart || 'Add to cart';
          }
        });
        
        // Also notify all product-form instances
        const productForms = document.querySelectorAll('product-form');
        productForms.forEach(form => {
          if (form.updateSubmitButton && typeof form.updateSubmitButton === 'function') {
            form.updateSubmitButton();
          }
        });

        this.closeModal();
      }

      goBack() {
        // Check if user is on prescription form or upload - these are sub-views of prescription step
        // Only check this if we're currently on the prescription step
        if (this.currentStepConfig?.type === 'prescription') {
          const prescriptionForm = document.getElementById(`PrescriptionFormForm-${this.sectionId}`);
          const prescriptionUpload = document.getElementById(`PrescriptionUpload-${this.sectionId}`);
          
          // Check if the form/upload elements are visible (not hidden)
          const isFormVisible = prescriptionForm && !prescriptionForm.closest('[hidden]') && prescriptionForm.offsetParent !== null;
          const isUploadVisible = prescriptionUpload && !prescriptionUpload.closest('[hidden]') && prescriptionUpload.offsetParent !== null;
          
          if (isFormVisible || isUploadVisible) {
            // Clear prescription data and method
            this.prescriptionData = null;
            this.prescriptionMethod = null;
            
            // Temporarily disable history tracking to avoid adding current step to history
            const wasTrackingHistory = this._trackingHistory;
            this._trackingHistory = false;
            
            // Show prescription method selection (upload/enter options)
            // This will restore the prescription step view
            this.showPrescriptionStep();
            
            // Restore history tracking
            this._trackingHistory = wasTrackingHistory;
            
            this.updateConfirmButton();
            return;
          }
        }
        
        // Fully dynamic back navigation using step history
        // If we have history, go back to the previous step
        if (this.stepHistory.length > 0) {
          const previous = this.stepHistory.pop();
          
          // Restore the previous step config and tags
          if (previous && previous.config) {
            // Restore tags to what they were at that step
            this.selectedTags = previous.tags || [];
            
            // Temporarily disable history tracking to prevent duplicate entries
            const wasTrackingHistory = this._trackingHistory;
            this._trackingHistory = false;
            
            // Set the config from history BEFORE showing to prevent it from being added to history
            // This ensures the show functions see the correct config and don't try to save it
            this.currentStepConfig = previous.config;
            
            // Check what type of step it was
            if (previous.config.type === 'prescription') {
              // Clear prescription data when going back
              this.prescriptionData = null;
              this.prescriptionMethod = null;
              this.showPrescriptionStep();
            } else if (previous.config.type === 'products') {
              this.selectedLens = null;
              this.selectedProductForVariant = null;
              this.showProductsStep();
            } else if (previous.config.type === 'variant' || previous.config.contentType === 'variant') {
              // Going back from variant step to products step
              this.selectedLens = null;
              this.selectedProductForVariant = null;
              // Need to go back one more step to get to products
              if (this.stepHistory.length > 0) {
                const productsStep = this.stepHistory.pop();
                this.currentStepConfig = productsStep.config;
                this.selectedTags = productsStep.tags || [];
                this.showProductsStep();
              } else {
                this.showProductsStep();
              }
            } else if (previous.config.type === 'initial') {
              // Go back to initial step (step 1)
              this.selectedPath = [];
              this.selectedTags = [];
              this.selectedLens = null;
              this.prescriptionData = null;
              this.prescriptionMethod = null;
              this.currentStepConfig = { type: 'initial' }; // Set to initial, not null
              this.showStepByType('initial');
            } else if (previous.config.options) {
              // It's a dynamic step with options
              this.showDynamicStep(previous.config);
            } else {
              // Unknown step type - fallback to initial
              this.currentStepConfig = { type: 'initial' };
              this.showStepByType('initial');
            }
            
            // Restore history tracking
            this._trackingHistory = wasTrackingHistory;
          } else {
            console.warn('goBack: Invalid history entry', previous);
          }
        } else {
          // No history - go back to initial step (reset everything)
          this.selectedPath = [];
          this.selectedTags = [];
          this.selectedLens = null;
          this.prescriptionData = null;
          this.prescriptionMethod = null;
          this.currentStepConfig = { type: 'initial' }; // Set to initial, not null
          this.showStepByType('initial');
        }
        
        this.updateConfirmButton();
      }


      // Show step by type instead of name - fully dynamic, no step names needed
      showStepByType(stepType) {
        const modal = this.getModal();
        if (!modal) return;
        
        // Hide all steps
        modal.querySelectorAll('.lens-selector__step').forEach(step => {
          step.hidden = true;
        });
        
        // For initial step, render from JSON config instead of static HTML
        if (stepType === 'initial') {
          // Build initial step config from JSON paths (easy-lens, prescription, non-prescription)
          if (this.config && this.config.paths) {
            const initialOptions = [];
            
            // Add the three main paths
            const paths = ['easy-lens', 'prescription', 'non-prescription'];
            paths.forEach(path => {
              const pathConfig = this.config.paths[path];
              if (pathConfig) {
                initialOptions.push({
                  path: path,
                  title: pathConfig.title || path,
                  image: pathConfig.image || '',
                  tags: pathConfig.tags || [],
                  description: pathConfig.description || '', // If available in config
                  nextStep: pathConfig.nextStep
                });
              }
            });
            
            // Create a temporary step config for the initial step
            const initialStepConfig = {
              type: 'initial',
              title: window.lensStrings?.select_lenses || 'Select Your Lenses',
              options: initialOptions
            };
            
            // Render it using dynamic step renderer but with the dynamic container
            const stepTitle = document.getElementById(`LensSelectorDynamicStepTitle-${this.sectionId}`);
            const stepOptions = document.getElementById(`LensSelectorDynamicStepOptions-${this.sectionId}`);
            const dynamicStep = modal ? modal.querySelector('[data-step="dynamic"]') : null;
            
            // Update title
            if (stepTitle) {
              stepTitle.textContent = initialStepConfig.title;
            }

            // Render options from JSON
            if (stepOptions) {
              stepOptions.className = 'lens-selector__options';
              stepOptions.innerHTML = '';
              
              initialStepConfig.options.forEach(option => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'lens-selector__option';
                btn.dataset.path = option.path;
                btn.dataset.tags = option.tags?.join(',') || '';
                btn.innerHTML = `
                  ${option.image ? `<img src="${option.image}" alt="${option.title}" class="lens-selector__option-image">` : ''}
                  <span class="lens-selector__option-title">${option.title}</span>
                  ${option.description ? `<span class="lens-selector__option-description">${option.description}</span>` : ''}
                `;
                btn.addEventListener('click', () => {
                  const tags = option.tags || [];
                  this.selectPath(option.path, tags);
                });
                stepOptions.appendChild(btn);
              });
            }

            // Hide all steps and show dynamic
            if (dynamicStep) {
              dynamicStep.hidden = false;
              dynamicStep.removeAttribute('hidden');
            }
          } else {
            // Fallback: show static step 1 if config not loaded
            const step = modal.querySelector('.lens-selector__step[data-step="1"]');
            if (step) {
              step.hidden = false;
            }
          }
        } else {
          // Show the appropriate step based on type
          let stepSelector = null;
          switch(stepType) {
            case 'dynamic':
              stepSelector = '.lens-selector__step[data-step="dynamic"]';
              break;
            case 'prescription':
              stepSelector = '.lens-selector__step[data-step="5"]';
              break;
            case 'products':
              stepSelector = '.lens-selector__step[data-step="6"]';
              break;
          }
          
          if (stepSelector) {
            const step = modal.querySelector(stepSelector);
            if (step) {
              step.hidden = false;
            }
          }
        }
      }


      // Fully dynamic step navigation using nextStep from JSON config
      // nextStep now contains the step object directly (no step name keys needed)
      navigateToNextStep(option, currentPathConfig) {
        // Store current step in history before navigating (unless we're going back)
        // Note: Don't add to history here if we're navigating from a dynamic step,
        // as showDynamicStep/showPrescriptionStep/showProductsStep will handle it
        // Only add here if we're navigating from a non-step context
        if (this._trackingHistory !== false && this.currentStepConfig && 
            this.currentStepConfig.type !== 'dynamic' && 
            !this.currentStepConfig.options) {
          this.stepHistory.push({
            config: this.currentStepConfig,
            tags: [...this.selectedTags] // Store tags at this point
          });
        }
        
        // Fully dynamic flow:
        // 1. If nextStep is explicitly defined, use it
        // 2. If nextStep is a string ("prescription" or "products"), show that step
        // 3. If nextStep is an object with options, show that step
        // 4. If no nextStep, check requiresPrescription from path config
        // 5. If requiresPrescription is true, show prescription step
        // 6. Otherwise, show products directly
        
        if (option.nextStep) {
          // Check if it's a special step type (string)
          if (typeof option.nextStep === 'string') {
            if (option.nextStep === 'prescription' || option.nextStep === 'step5') {
              this.showPrescriptionStep();
              return;
            }
            if (option.nextStep === 'products' || option.nextStep === 'step6') {
              this.showProductsStep();
              return;
            }
          }
          
          // Otherwise, nextStep is a step config object with options
          if (typeof option.nextStep === 'object' && option.nextStep !== null) {
            if (option.nextStep.options && Array.isArray(option.nextStep.options)) {
              // It's a step config with options - show it
              this.showDynamicStep(option.nextStep);
              return;
            }
          }
        }
        
        // No nextStep defined or nextStep is invalid - check if prescription is required
        // Use the path config's requiresPrescription flag
        if (currentPathConfig?.requiresPrescription) {
          this.showPrescriptionStep();
        } else {
          this.showProductsStep();
        }
      }

      // Navigate directly to a step config (no step names needed)
      showPrescriptionStep() {
        // Store current step in history before navigating (unless we're going back)
        // Only add to history if:
        // 1. History tracking is enabled
        // 2. We have a current step config
        // 3. The current step config is NOT already a prescription step (to avoid duplicates when restoring)
        if (this._trackingHistory !== false && this.currentStepConfig && 
            this.currentStepConfig.type !== 'prescription') {
          this.stepHistory.push({
            config: this.currentStepConfig,
            tags: [...this.selectedTags] // Store tags at this point
          });
        }
        
        this.currentStepConfig = { type: 'prescription' };
        
        const step5Title = document.getElementById(`LensSelectorStep5Title-${this.sectionId}`);
        const prescriptionMethodContainer = document.getElementById(`LensSelectorPrescriptionMethod-${this.sectionId}`);
        
        if (step5Title) {
          step5Title.textContent = window.lensStrings?.prescription_method || 'Choose Prescription Method';
        }
        
        if (prescriptionMethodContainer) {
          prescriptionMethodContainer.innerHTML = `
            <div class="lens-selector__options">
              <button type="button" class="lens-selector__option" data-method="upload">
                <span class="lens-selector__option-title">${window.lensStrings?.upload_prescription || 'Upload Prescription'}</span>
                <span class="lens-selector__option-description">${window.lensStrings?.upload_instructions || 'Upload a clear image of your prescription'}</span>
              </button>
              <button type="button" class="lens-selector__option" data-method="enter">
                <span class="lens-selector__option-title">${window.lensStrings?.enter_prescription || 'Enter Prescription'}</span>
                <span class="lens-selector__option-description">${window.lensStrings?.enter_manually || 'Enter prescription details manually'}</span>
              </button>
            </div>
          `;
          
          // Bind events
          prescriptionMethodContainer.querySelectorAll('[data-method]').forEach(btn => {
            btn.addEventListener('click', () => {
              const method = btn.dataset.method;
              this.prescriptionMethod = method;
              // Ensure prescription step config is maintained when showing upload/form
              // This ensures the prescription step is saved to history when navigating to products
              if (!this.currentStepConfig || this.currentStepConfig.type !== 'prescription') {
                this.currentStepConfig = { type: 'prescription' };
              }
              if (method === 'upload') {
                this.showPrescriptionUpload();
              } else {
                this.showPrescriptionForm();
              }
            });
          });
        }
        
        this.showStepByType('prescription');
        this.currentStepConfig = { type: 'prescription' };
      }

      showProductsStep() {
        // Store current step in history before navigating (unless we're going back)
        if (this._trackingHistory !== false && this.currentStepConfig) {
          this.stepHistory.push({
            config: this.currentStepConfig,
            tags: [...this.selectedTags] // Store tags at this point
          });
        }
        
        // Set current step config before loading products
        this.currentStepConfig = { type: 'products' };
        
        this.loadLensProducts();
      }

      showVariantStep(product, defaultVariant, defaultImageUrl) {
        // Create step config for variant selection and use dynamic step system
        const variantStepConfig = {
          type: 'variant',
          contentType: 'variant',
          title: product.title,
          product: product,
          defaultVariant: defaultVariant,
          defaultImageUrl: defaultImageUrl
        };

        // Use the dynamic step system
        this.showDynamicStep(variantStepConfig);
      }

      renderVariantContentInDynamicStep(container, stepConfig) {
        const product = stepConfig.product;
        const defaultImageUrl = stepConfig.defaultImageUrl;

        // Get variants and filter to only in-stock variants
        const allVariants = product.variants || [];
        const variants = allVariants.filter(v => v.available === true);
        
        if (variants.length === 0) {
          container.innerHTML = `
            <div class="lens-selector__error">
              <p>${window.lensStrings?.noProducts || 'No in-stock variants found.'}</p>
            </div>
          `;
          return;
        }

        // Get base product image
        let baseImageUrl = defaultImageUrl || '';
        if (!baseImageUrl && product.images && product.images.length > 0) {
          const firstImage = product.images[0];
          if (typeof firstImage === 'string') {
            baseImageUrl = firstImage;
          } else if (firstImage.src) {
            baseImageUrl = firstImage.src;
          } else if (firstImage.url) {
            baseImageUrl = firstImage.url;
          }
        }

        // Optimize base image URL if it's a Shopify CDN URL
        if (baseImageUrl && baseImageUrl.includes('cdn.shopify.com')) {
          baseImageUrl = baseImageUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048)\./, '_300x300.');
          if (!baseImageUrl.match(/_\d+x\d+\./)) {
            baseImageUrl = baseImageUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_300x300.$1$2');
          }
        }

        // Create products grid for variants
        const grid = document.createElement('div');
        grid.className = 'lens-selector__products-grid';

        // Create a product card for each variant
        variants.forEach(variant => {
          const card = document.createElement('div');
          card.className = 'lens-selector__product-card';
          card.dataset.productId = product.id;
          card.dataset.variantId = variant.id;
          
          // Use variant image if available, otherwise use product image
          let imageUrl = variant.image || baseImageUrl;
          
          // Optimize variant image URL if it's a Shopify CDN URL
          if (imageUrl && imageUrl.includes('cdn.shopify.com')) {
            imageUrl = imageUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048)\./, '_300x300.');
            if (!imageUrl.match(/_\d+x\d+\./)) {
              imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_300x300.$1$2');
            }
          }

          // Format price for variant
          const variantPriceInCents = this.convertPriceToCents(variant.price);
          const formattedPrice = this.formatMoney(variantPriceInCents);

          // Build variant title - use variant title if it's not "Default Title", otherwise use product title + variant option
          let variantTitle = product.title;
          if (variant.title && variant.title !== 'Default Title') {
            variantTitle = variant.title;
          } else {
            const variantOption = variant.option1 || variant.option2 || variant.option3;
            if (variantOption) {
              variantTitle = `${product.title} - ${variantOption}`;
            }
          }

          card.innerHTML = `
            <div class="lens-selector__product-image-wrapper">
              ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(variantTitle)}" class="lens-selector__product-image" data-product-id="${product.id}" data-variant-id="${variant.id}" loading="lazy" onerror="this.style.display='none'">` : '<div class="lens-selector__product-image-placeholder"></div>'}
            </div>
            <div class="lens-selector__product-info">
              <h4 class="lens-selector__product-title">${this.escapeHtml(variantTitle)}</h4>
              <p class="lens-selector__product-price" data-product-id="${product.id}" data-variant-id="${variant.id}">${formattedPrice || 'Price unavailable'}</p>
            </div>
            <button type="button" class="lens-selector__product-select-btn" data-product-id="${product.id}" data-variant-id="${variant.id}">
              ${window.lensStrings?.select || 'Select'}
            </button>
          `;

          // Handle variant product selection - directly select the variant
          const selectBtn = card.querySelector('.lens-selector__product-select-btn');
          if (selectBtn) {
            selectBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              // Directly select this variant
              this.selectLens({
                id: product.id,
                variantId: variant.id,
                title: variantTitle,
                variantTitle: variant.title !== 'Default Title' ? variant.title : null,
                price: variantPriceInCents,
                image: imageUrl
              });
            });
          }

          grid.appendChild(card);
        });

        container.innerHTML = '';
        container.appendChild(grid);
        this.updateConfirmButton();
      }

      formatMoney(cents) {
        if (cents === null || cents === undefined || isNaN(cents)) {
          console.warn('formatMoney: Invalid cents value', cents);
          return '$0.00';
        }
        
        // Ensure cents is a number
        cents = parseInt(cents, 10);
        if (isNaN(cents)) {
          return '$0.00';
        }
        
        // Use theme's currency formatter if available - this is the primary method
        if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
          try {
            const moneyFormat = window.theme.settings?.moneyFormat;
            if (moneyFormat) {
              const formatted = window.theme.Currency.formatMoney(cents, moneyFormat);
              
              // Check if formatter actually worked (contains numbers and no placeholders)
              const hasNumbers = /\d/.test(formatted);
              const hasPlaceholders = formatted.includes('{{');
              
              if (hasNumbers && !hasPlaceholders) {
                // Formatter worked correctly
                return formatted;
              }
              
              // If formatter returned the format string unchanged or has placeholders, do manual replacement
              if (formatted === moneyFormat || hasPlaceholders || !hasNumbers) {
                // Manual replacement as fallback
                const amount = (cents / 100).toFixed(2);
                // Add thousand separators
                const parts = amount.split('.');
                const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                const formattedAmount = parts.length > 1 ? `${dollars}.${parts[1]}` : dollars;
                
                // Replace all variations of {{amount}} placeholder
                let result = moneyFormat;
                result = result.replace(/\{\{\s*amount\s*\}\}/gi, formattedAmount);
                result = result.replace(/\{\{amount\}\}/gi, formattedAmount);
                
                // If replacement worked, return it
                if (result && result !== moneyFormat && !result.includes('{{')) {
                  return result;
                }
                
                // If replacement failed, at least return currency + amount
                const currencySymbol = moneyFormat.replace(/\{\{.*\}\}/gi, '').trim() || '$';
                return `${currencySymbol}${formattedAmount}`;
              }
            }
            
            // Try without format parameter (uses default from theme)
            const formattedDefault = window.theme.Currency.formatMoney(cents);
            if (formattedDefault && formattedDefault.trim() !== '' && /\d/.test(formattedDefault) && !formattedDefault.includes('{{')) {
              return formattedDefault;
            }
          } catch (e) {
            console.warn('Error using theme formatter:', e);
          }
        }
        
        // Fallback to Shopify's formatter
        if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
          try {
            const moneyFormat = window.money_format || window.theme?.settings?.moneyFormat || '${{amount}}';
            const formatted = window.Shopify.formatMoney(cents, moneyFormat);
            if (formatted && formatted.trim() !== '' && !formatted.includes('{{')) {
              return formatted;
            }
          } catch (e) {
            console.warn('Error using Shopify formatter:', e);
          }
        }
        
        // Last resort: basic formatting with currency detection
        const amount = (cents / 100).toFixed(2);
        // Try to extract currency from money format or use default
        let currencySymbol = '$';
        if (window.theme?.settings?.moneyFormat) {
          const format = window.theme.settings.moneyFormat;
          // Extract currency symbol from format (e.g., "${{amount}}" -> "$")
          const symbolMatch = format.match(/^([^\{]+)/);
          if (symbolMatch) {
            currencySymbol = symbolMatch[1].trim();
          }
        } else if (window.Shopify?.currency?.active) {
          // Use Shopify currency if available
          const currency = window.Shopify.currency.active;
          currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '';
        }
        return `${currencySymbol}${amount}`;
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      getSelectedLens() {
        return this.selectedLens;
      }

      getSelectedTags() {
        return this.selectedTags;
      }
    }
  );
}

